import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { DisplayImage } from '@repo/ui/file-input'
import { BookmarkIcon, PdfIcon, PlusIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '../../api/apis.config'
import CountryFlag from '../../components/elements/country-flag'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import UserNavBar from '../../components/elements/nav-bar'
import { QualificationTags } from '../../components/elements/qualification-badge'
import { Paths } from '../../lib/constants/path'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { accomodationEnumMapping } from '../../lib/types/enums/accomodation-type/accomodation-type'
import { staffPositionEnumMapping } from '../../lib/types/enums/staff/staff-position.enum'
import { userTypeEnumMapping } from '../../lib/types/enums/user/user-type.enum'
import { Guide } from '../../lib/types/models/guide/guide'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'

const GuideListPage = () => {
  const { t } = useTranslation()
  const { selectedData, modalState, toggleModal, closeAll } = useModalManager<'view-pdf', Guide>([
    'view-pdf'
  ])
  const tableRef = useRef<DynamicTableRef<Guide>>(null)
  const { navigate, navigateWithEvent } = useNavigator()

  const columns: Column<Guide>[] = [
    {
      accessorKey: 'name',
      header: 'Nom',
      enableSorting: true
    },
    {
      accessorKey: 'pdf',
      header: 'PDF',
      cell: ({ row }) => {
        const pdfPath = row.original.pdf.path
        if (!pdfPath) return '-'

        return (
          <Button
            type="button"
            icon={PdfIcon}
            variant="ghost"
            size="small"
            className="px-1"
            onClick={(e) => {
              e.stopPropagation()
              toggleModal('view-pdf', row.original)
            }}
          />
        )
      }
    },
    {
      accessorKey: 'countries',
      header: 'Pays',
      cell: ({ row }) => {
        const countries = row.original.countries
        if (!countries || countries.length === 0) return '-'

        return (
          <div className={'grid grid-cols-4 gap-x-5 gap-y-1 py-3'}>
            {countries?.map(({ code }) => (
              <div className="w-10" key={code}>
                <CountryFlag code={code} />
              </div>
            ))}
          </div>
        )
      }
    },

    {
      accessorKey: 'formulas',
      header: 'Formules',
      cell: ({ row }) => {
        const formulas = row.original.formulas
        if (!formulas || formulas.length === 0) return '-'

        return (
          <div className={'flex flex-col gap-1 py-2'}>
            {formulas?.map((formula) => (
              <Typography key={getTranslatedName(formula)} variant={'secondary-body'}>
                {getTranslatedName(formula)}
              </Typography>
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'options',
      header: 'Options',
      cell: ({ row }) => {
        const options = row.original.options
        if (!options || options.length === 0) return '-'

        return (
          <div className={'flex flex-col gap-1 py-2'}>
            {options?.map((option) => (
              <Typography key={getTranslatedName(option)} variant={'secondary-body'}>
                {getTranslatedName(option)}
              </Typography>
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'accomodationTypes',
      header: 'Type(s) d’hébergements',
      cell: ({ row }) => {
        const accomodationTypes = row.original.accomodationTypes
        if (!accomodationTypes || accomodationTypes.length === 0) return '-'

        return (
          <div className={'flex flex-col gap-1 py-2'}>
            {accomodationTypes?.map((accomodationType) => (
              <Typography key={accomodationType} variant={'secondary-body'}>
                {t(accomodationEnumMapping[accomodationType])}
              </Typography>
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'staffPositions',
      header: 'Type(s) de postes',
      cell: ({ row }) => {
        const staffPositions = row.original.staffPositions
        if (!staffPositions || staffPositions.length === 0) return '-'

        return (
          <div className={'flex flex-col gap-1 py-2'}>
            {staffPositions?.map((staffPosition) => (
              <Typography key={staffPosition} variant={'secondary-body'}>
                {t(staffPositionEnumMapping[staffPosition])}
              </Typography>
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'qualifications',
      header: 'Qualifications',
      cell: ({ row }) => {
        const qualifications = row.original.qualifications
        return <QualificationTags qualifications={qualifications ?? []} />
      }
    },
    {
      accessorKey: 'userType',
      header: 'Catégorie d’utilisateur concernés',
      cell: ({ row }) => {
        const userType = row.original.userType
        if (!userType) return '-'

        return (
          <div className={'flex flex-wrap gap-1'}>
            <Badge
              key={userType}
              text={userTypeEnumMapping[userType].label}
              variant={userTypeEnumMapping[userType].variant}
            />
          </div>
        )
      }
    }
  ]

  return (
    <div>
      <UserNavBar title={'Guides LEC'} icon={BookmarkIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<Guide>
        ref={tableRef}
        title={'Tous les guides'}
        columns={columns}
        deleteMessage={{
          name: "ce guide avec l'ID ",
          attributeName: 'id',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer tous ces guides ?',
          deleteMany: (data) => {
            return `Êtes-vous sûr de vouloir supprimer ces guides avec les ID "${data
              .map((item) => item.id)
              .join('", "')}"?`
          }
        }}
        onRowClick={(row, event) =>
          navigateWithEvent(Paths.GUIDE_UPDATE_PAGE.replace(':id', row.id.toString()), event)
        }
        selectable
        allowSearchParam
        endpoint={API.guides.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer',
            fn: () => navigate(Paths.GUIDE_CREATE_PAGE)
          }
        ]}
      />

      {selectedData && (
        <Modal
          isOpen={modalState['view-pdf']}
          onCancel={closeAll}
          title={selectedData.pdf?.name}
          hideActions
          size={'large'}
        >
          <DisplayImage
            allowAll
            type={selectedData.pdf?.type}
            src={selectedData.pdf?.path}
            className={'h-[70vh] w-full rounded-sm object-contain relative z-10 overflow-auto'}
          />
        </Modal>
      )}
    </div>
  )
}

export default GuideListPage
